import { type SxProps, type Theme } from "@mui/material/styles";

import { Box } from "ui/box";
import { Select } from "ui/select";
import { Dialog } from "ui/dialog";
import VersionCompareColumn from "components/modals/compare-versions-modal/components/version-compare-column";

import { useCompareVersions } from "components/modals/compare-versions-modal/hooks/use-compare-versions";

/**
 * The version pickers stay put while the two previews scroll beneath them, so the comparison owns its
 * own scroll box and fills the body's height rather than letting the whole body scroll.
 */
const COMPARISON_SX: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minHeight: 0,
};

/** Half its column, as the legacy `w-1/2` drew it. */
const VERSION_SELECT_SX: SxProps<Theme> = { width: "50%" };

interface CompareVersionsModalProps {
  isOpen: boolean;
  setIsOpen?: (isOpen: boolean) => void;
  touchpointVersionId: string;
  hideSubjectAndPreheader?: boolean;
}

const CompareVersionsModal = ({
  isOpen,
  setIsOpen = () => {},
  touchpointVersionId,
  hideSubjectAndPreheader = false,
}: CompareVersionsModalProps) => {
  const {
    touchpointData,
    isLoadingLeft,
    isLoadingRight,
    selectedLeftVersionId,
    selectedRightVersionId,
    leftVersionSelectOptions,
    rightVersionSelectOptions,
    isEmailType,
    handleLeftVersionChange,
    handleRightVersionChange,
  } = useCompareVersions({
    touchpointVersionId,
    isOpen,
  });

  if (!isOpen) {
    return null;
  }

  const hasLeftVersion =
    !isLoadingLeft && !!touchpointData.leftVersion?.html && !!touchpointData.leftVersion?.beeJson;
  const hasRightVersion =
    !isLoadingRight &&
    !!touchpointData.rightVersion?.html &&
    !!touchpointData.rightVersion?.beeJson;
  const showLeftError = !isLoadingLeft && !hasLeftVersion;
  const showRightError = !isLoadingRight && !hasRightVersion;

  return (
    <Dialog
      open
      onClose={() => setIsOpen(false)}
      size="large"
      width={900}
      headline="Compare creative versions">
      <Box sx={COMPARISON_SX}>
        <div className="flex justify-center gap-4 pr-3 pl-1 pt-1 pb-2 min-w-0">
          <div className="flex-1 min-w-0 max-w-[400px]">
            <Select
              size="small"
              selectName="left-version-select"
              disableClearable
              onChange={(newValue) => newValue && handleLeftVersionChange(newValue)}
              options={leftVersionSelectOptions}
              value={leftVersionSelectOptions.find(
                (option) => option.value === selectedLeftVersionId,
              )}
              ariaLabel="Select left version to compare"
              sx={VERSION_SELECT_SX}
            />
          </div>
          <div className="flex-1 min-w-0 max-w-[400px]">
            <Select
              size="small"
              selectName="right-version-select"
              disableClearable
              onChange={(newValue) => newValue && handleRightVersionChange(newValue)}
              options={rightVersionSelectOptions}
              value={rightVersionSelectOptions.find(
                (option) => option.value === selectedRightVersionId,
              )}
              ariaLabel="Select right version to compare"
              sx={VERSION_SELECT_SX}
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-scroll custom-scroll min-h-0">
          <div className="flex justify-center gap-4 p-1 pt-0 min-w-0">
            <VersionCompareColumn
              isLoading={isLoadingLeft}
              hasVersion={hasLeftVersion}
              showError={showLeftError}
              version={touchpointData.leftVersion}
              isEmailType={isEmailType}
              side="left"
              hideSubjectAndPreheader={hideSubjectAndPreheader}
            />
            <VersionCompareColumn
              isLoading={isLoadingRight}
              hasVersion={hasRightVersion}
              showError={showRightError}
              version={touchpointData.rightVersion}
              isEmailType={isEmailType}
              side="right"
              hideSubjectAndPreheader={hideSubjectAndPreheader}
            />
          </div>
        </div>
      </Box>
    </Dialog>
  );
};

export default CompareVersionsModal;
